import { Fragment, useEffect, useState } from 'react'
import { Popover, Transition } from '@headlessui/react'
import moment from 'moment/moment'
import { FaRegCalendarAlt } from 'react-icons/fa'
import { DateRangePicker as DatePicker } from 'react-date-range'

import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'

import { Cart } from '../Ui'
import Button from './Button'

const getFinancialYearRanges = () => {
    const now = moment()
    const currentMonth = now.month()
    const currentYear = now.year()

    // This month and Previous month
    const thisMonthStart = moment().startOf('month')
    const thisMonthEnd = moment().endOf('month')
    const prevMonthStart = moment().subtract(1, 'month').startOf('month')
    const prevMonthEnd = moment().subtract(1, 'month').endOf('month')

    // Financial year: April to March
    let fyStart, fyEnd, prevFyStart, prevFyEnd
    if (currentMonth < 3) {
        fyStart = moment()
            .year(currentYear - 1)
            .month(3)
            .startOf('month')
        fyEnd = moment().year(currentYear).month(2).endOf('month')
        prevFyStart = moment()
            .year(currentYear - 2)
            .month(3)
            .startOf('month')
        prevFyEnd = moment()
            .year(currentYear - 1)
            .month(2)
            .endOf('month')
    } else {
        fyStart = moment().year(currentYear).month(3).startOf('month')
        fyEnd = moment()
            .year(currentYear + 1)
            .month(2)
            .endOf('month')
        prevFyStart = moment()
            .year(currentYear - 1)
            .month(3)
            .startOf('month')
        prevFyEnd = moment().year(currentYear).month(2).endOf('month')
    }

    return [
        {
            label: 'This Week',
            range: () => ({
                startDate: moment().startOf('week').toDate(),
                endDate: moment().endOf('week').toDate(),
            }),
            isSelected: range => {
                const { startDate, endDate } = range
                return (
                    moment(startDate).isSame(moment().startOf('week'), 'day') &&
                    moment(endDate).isSame(moment().endOf('week'), 'day')
                )
            },
        },
        {
            label: 'Last Week',
            range: () => ({
                startDate: moment().subtract(1, 'week').startOf('week').toDate(),
                endDate: moment().subtract(1, 'week').endOf('week').toDate(),
            }),
            isSelected: range => {
                const { startDate, endDate } = range
                return (
                    moment(startDate).isSame(moment().subtract(1, 'week').startOf('week'), 'day') &&
                    moment(endDate).isSame(moment().subtract(1, 'week').endOf('week'), 'day')
                )
            },
        },
        {
            label: 'This Month',
            range: () => ({
                startDate: thisMonthStart.toDate(),
                endDate: thisMonthEnd.toDate(),
            }),
            isSelected: range => {
                const { startDate, endDate } = range
                return moment(startDate).isSame(thisMonthStart, 'day') && moment(endDate).isSame(thisMonthEnd, 'day')
            },
        },
        {
            label: 'Last Month',
            range: () => ({
                startDate: prevMonthStart.toDate(),
                endDate: prevMonthEnd.toDate(),
            }),
            isSelected: range => {
                const { startDate, endDate } = range
                return moment(startDate).isSame(prevMonthStart, 'day') && moment(endDate).isSame(prevMonthEnd, 'day')
            },
        },
        {
            label: 'This FY (Apr-Mar)',
            range: () => ({
                startDate: fyStart.toDate(),
                endDate: fyEnd.toDate(),
            }),
            isSelected: range => {
                const { startDate, endDate } = range
                return moment(startDate).isSame(fyStart, 'day') && moment(endDate).isSame(fyEnd, 'day')
            },
        },
        {
            label: 'Last FY (Apr-Mar)',
            range: () => ({
                startDate: prevFyStart.toDate(),
                endDate: prevFyEnd.toDate(),
            }),
            isSelected: range => {
                const { startDate, endDate } = range
                return moment(startDate).isSame(prevFyStart, 'day') && moment(endDate).isSame(prevFyEnd, 'day')
            },
        },
    ]
}

export default function DateRangePicker({
    id = '',
    label = '',
    className = '',
    panelClassName = '',
    error = '',
    startDate,
    endDate,
    saveDate,
    ...props
}) {
    const [date, setDate] = useState()

    const resetDate = () => {
        setDate({
            startDate: new Date(startDate),
            endDate: new Date(endDate),
            key: 'selection',
        })
    }
    useEffect(() => {
        resetDate()
        // eslint-disable-next-line
    }, [startDate, endDate])

    return (
        <div className={className}>
            {label && (
                <label htmlFor={id} className={`block mb-1 font-medium ${error ? 'text-danger' : 'text-black dark:text-white'}`}>
                    {label}
                </label>
            )}
            <Popover className="relative">
                {({ close }) => (
                    <>
                        <Popover.Button
                            className={`flex flex-row items-center gap-2 custom-input-date w-full flex-auto rounded-lg border border-stroke bg-transparent py-2 px-3 outline-none focus:border-primary dark:border-strokedark dark:bg-strokedark dark:focus:border-secondary`}
                        >
                            <FaRegCalendarAlt /> {startDate ? moment(startDate).format('ll') : 'Start Date'} -{' '}
                            {endDate ? moment(endDate).format('ll') : 'End Date'}
                        </Popover.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                        >
                            <Popover.Panel className={`absolute ${panelClassName} z-10`}>
                                <Cart divclass="">
                                    <DatePicker
                                        id={id}
                                        {...props}
                                        staticRanges={getFinancialYearRanges()}
                                        ranges={[date]}
                                        onChange={e => setDate(e.selection)}
                                    />
                                    <div className="flex items-center gap-4 p-3">
                                        <Button
                                            type="button"
                                            className="w-full text-white border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                            onClick={() => {
                                                close()
                                                saveDate(
                                                    moment(date.startDate).locale('en').format('YYYY-MM-DD'),
                                                    moment(date.endDate).locale('en').format('YYYY-MM-DD')
                                                )
                                            }}
                                        >
                                            Confirm
                                        </Button>
                                        <Button
                                            type="button"
                                            className="w-full text-white border-danger bg-danger"
                                            onClick={() => {
                                                close()
                                                resetDate()
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </Cart>
                            </Popover.Panel>
                        </Transition>
                    </>
                )}
            </Popover>
            {error && <p className="text-sm text-danger">{error}</p>}
        </div>
    )
}
